// /api/authApi.js

import { getActiveResortId } from '../utils/activeResort'
import { invalidateAllSubfoldersCache } from '../utils/operationalFolderCache.ts'
import api from './api'

const SENSITIVE_USER_KEYS = [
  'password',
  'refreshToken',
  'accessToken',
  'passwordResetCode',
  'emailChangeCode',
  'newEmail',
  'emailChangeExpiresAt',
]

export function buildUserForLocalStorage(
  user,
  permissions = [],
  enabledModules = [],
  resortIds = [],
  resorts = [],
) {
  if (!user) return null
  const sanitized = { ...user }
  SENSITIVE_USER_KEYS.forEach(key => delete sanitized[key])
  if (sanitized.Role && typeof sanitized.Role === 'object') {
    sanitized.Role = { ...sanitized.Role }
    delete sanitized.Role.permissions
  }
  // user.resort_id est lu 164 fois dans le front : la bascule le reecrit plutot
  // que de migrer ces lectures, il vaut donc « site actif », plus « site d attache ».
  const active = getActiveResortId()
  if (active !== null) {
    sanitized.resort_id = active
  }
  return {
    user: sanitized,
    permissions: Array.isArray(permissions) ? permissions : [],
    enabledModules: Array.isArray(enabledModules) ? enabledModules : [],
    resortIds: Array.isArray(resortIds) ? resortIds : [],
    resorts: Array.isArray(resorts) ? resorts : [],
  }
}

export function persistUserToLocalStorage(
  user,
  permissions,
  enabledModules,
  resortIds,
  resorts,
) {
  invalidateAllSubfoldersCache()
  try {
    const payload = buildUserForLocalStorage(
      user,
      permissions,
      enabledModules,
      resortIds,
      resorts,
    )
    if (payload) {
      localStorage.setItem('user', JSON.stringify(payload))
    } else {
      localStorage.removeItem('user')
    }
  } catch (e) {
    console.warn('Could not persist user to localStorage', e)
  }
}

// a stored user means the session check will most likely succeed
export function hasStoredUser() {
  try {
    return localStorage.getItem('user') != null
  } catch {
    return false
  }
}

export function clearUserFromLocalStorage() {
  invalidateAllSubfoldersCache()
  localStorage.removeItem('user')
}

// the SSO buttons are plain links, the backend runs the whole OIDC flow
export const ssoLoginUrl = (provider, remember) =>
  `${(import.meta.env.VITE_URL || '').replace(/\/$/, '')}/auth/sso/${provider}?remember=${remember ? 1 : 0}`

export const login = async credentials => {
  const response = await api.post('/users/login', credentials)
  const data = response.data
  if (data.success === false) {
    const err = new Error(data.error || 'Échec de connexion')
    err.isInactive = data.isInactive
    throw err
  }
  if (!data.user) {
    throw new Error('Invalid login response')
  }
  await saveConnexionHistory(data.user)
  return data
}

export const logout = async () => {
  await api.post('/users/logout')
  return true
}

export const getMe = async () => {
  const response = await api.get('/users/me')
  return response.data
}

// Reset password
export const resetPassword = async (token, newPassword) => {
  const response = await api.post('/users/resetPassword', {
    token,
    newPassword,
  })
  return response.data
}

// Request code for password reset
export const requestPasswordCode = async email => {
  const response = await api.post('/users/requestPasswordChange', { email })
  return response.data
}

// Verify code
export const verifycode = async (email, code) => {
  const response = await api.post('/users/verifyCode', { email, code })
  return response.data
}

// Change password
export const changePassword = async (email, token, newPassword) => {
  const response = await api.post('/users/changePassword', {
    email,
    token,
    newPassword,
  })
  return response.data
}

// Save connection history (user from login response or getMe)
export const saveConnexionHistory = async user => {
  if (!user?.id) return
  try {
    const now = new Date()

    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    const localDate = `${year}-${month}-${day}`
    const localTime = `${hours}:${minutes}`

    await api.post('/connexion_history/Add', {
      user_id: user.id,
      connexion_date: localDate,
      connexion_time: localTime,
      resort_id: user.resort_id,
    })
  } catch (err) {
    console.error('Error saving connection history:', err)
    throw err
  }
}

export const connexion_history = async (filters = {}) => {
  try {
    const params = new URLSearchParams()
    if (filters.startDate) params.append('startDate', filters.startDate)
    if (filters.endDate) params.append('endDate', filters.endDate)
    if (filters.resortId) params.append('resortId', filters.resortId)
    if (filters.userId) params.append('userId', filters.userId)

    const queryString = params.toString()
    const url = `/connexion_history${queryString ? `?${queryString}` : ''}`
    const response = await api.get(url)
    return response.data
  } catch (err) {
    console.error('Error fetching connexion history:', err)
    throw err
  }
}
